import React, { useState, useEffect, useRef, useMemo } from 'react';
import './Transactions.css';

// Type definitions
interface Token {
  address: string;
  name: string;
  symbol: string;
  logo: string | null;
  decimals: string;
}

interface Transaction {
  transactionHash: string;
  transactionIndex: number;
  transactionType: string;
  blockTimestamp: string;
  blockNumber: number;
  walletAddress: string;
  subCategory: string;
  baseTokenAmount: string;
  quoteTokenAmount: string;
  baseTokenPriceUsd: string;
  quoteTokenPriceUsd: string;
  baseQuotePrice: string;
  totalValueUsd: string;
  isNew?: boolean; // For animation
}

interface ApiResponse {
  page: number;
  cursor: string;
  pageSize: number;
  exchangeAddress: string;
  exchangeLogo: string | null;
  exchangeName: string | null;
  pairAddress: string;
  pairLabel: string;
  baseToken: Token;
  quoteToken: Token;
  result: Transaction[];
}

interface TransactionsProps {
  pair: any;
  chainId?: any;
}

const Transactions: React.FC<TransactionsProps> = ({ pair, chainId }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const chainHex = useMemo(() => {
    const id = typeof chainId === 'string'
      ? parseInt(chainId, 10)
      : chainId?.toNumber?.() ?? chainId;

    switch (id) {
      case 1:      return '0x1';
      case 56:     return '0x38';
      case 137:    return '0x89';
      case 42161:  return '0xa4b1';
      case 8453:   return '0x2105';
      case 501424: return 'solana';
      default:     return null;
    }
  }, [chainId]);

  const transactionHashesRef = useRef<Set<string>>(new Set());

  // Format timestamp to a relative time (e.g., "1 second ago")
  const formatTimeAgo = (dateString: string): string => {
    const now = new Date();
    const date = new Date(dateString);
    const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffSeconds < 60) {
      return `${diffSeconds}s ago`;
    }
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    }
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) {
      return `${diffDays}d ago`;
    }
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks < 4) {
      return `${diffWeeks}w ago`;
    }
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) {
      return `${diffMonths}M ago`;
    }
    const diffYears = Math.floor(diffDays / 365);
    return `${diffYears}y ago`;
  };

  // Fetch transactions from the API
  const fetchTransactions = async () => {
    try {
      if (!pair || !chainHex || !chainId) return;
      const now = new Date();
      const toDate = now.toISOString();
      let url;
      if (chainId === 501424) {
        url = `https://moralis.com/api/solana/web3/token/mainnet/pairs/${pair}/swaps?order=DESC&transactionTypes=buy%2Csell&toDate=${encodeURIComponent(toDate)}&limit=50`;
      } else {
        url = `https://moralis.com/api/web3/pairs/${pair}/swaps?chain=${chainHex}&order=DESC&transactionTypes=buy%2Csell&toDate=${encodeURIComponent(toDate)}&limit=50`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data: ApiResponse = await response.json();
      const newTransactions = data.result.filter(tx => !transactionHashesRef.current.has(tx.transactionHash));

      if (newTransactions.length > 0) {
        const transactionsWithFlag = newTransactions.map(tx => ({
          ...tx,
          isNew: true
        }));
        newTransactions.forEach(tx => transactionHashesRef.current.add(tx.transactionHash));
        setTransactions(prev => [...transactionsWithFlag, ...prev]);
        setTimeout(() => setTransactions(prev => prev.map(tx => ({ ...tx, isNew: false }))), 1000);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to fetch transactions. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    const intervalId = setInterval(() => fetchTransactions(), 1000);
    return () => clearInterval(intervalId);
  }, [pair, chainHex]);

  const getTypeStyle = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'buy': return 'transaction-buy';
      case 'sell': return 'transaction-sell';
      case 'addliquidity': return 'transaction-add';
      case 'removeliquidity': return 'transaction-remove';
      default: return '';
    }
  };

  const shouldShowNegative = (txType: string): boolean => txType.toLowerCase() === 'sell' || txType.toLowerCase() === 'removeliquidity';

  if (loading && transactions.length === 0) {
    return <div className="loading">Loading transactions...</div>;
  }

  if (error && transactions.length === 0) {
    return <div className="error">{error}</div>;
  }

  if (transactions.length === 0) {
    return <></>;
  }

  return (
    <div className="transactions-container">
      <div className="transactions-table-container">
        <table className="transactions-table">
          <tbody>
            {transactions.map(tx => (
              <tr
                key={tx.transactionHash}
                className={`transaction-row ${tx.isNew ? 'new-transaction' : ''} ${getTypeStyle(tx.transactionType)}`}
              >
                <td className="transaction-type">{tx.transactionType}</td>
                <td>{formatTimeAgo(tx.blockTimestamp)}</td>
                <td className={shouldShowNegative(tx.transactionType) ? 'negative-amount' : 'positive-amount'}>
                  {`$${parseFloat(tx.totalValueUsd).toFixed(2)}`}
                </td>
              </tr>
            ))}
            {transactions.length === 0 && (
              <tr>
                <td colSpan={5} className="no-transactions">Nothing found yet... </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Transactions;
